import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';

import useScrollHandler from '../../../../hooks/useScrollHandler/useScrollHandler';
import { Option } from '../../../types';
import { StyledCheckbox } from '../../StyledCheckbox/styled';
import { globalCloseEvents, tooltipStyles } from '../constants';
import { StyledFilterIcon, StyledFilterWrapper } from '../MinMaxFilter/styled';
import { StyledCheckboxWrapper, StyledSelectAll } from './styled';

type Props = {
  elementId: string;
  allItems: Option[];
  selectedItems: string[];
  selectAll?: boolean;
  onChange: (selectedItems: string[]) => void;
};

const MultiSelectFilter = ({ elementId, allItems, selectedItems, onChange, selectAll }: Props): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  useScrollHandler(() => setIsOpen(false));

  const handleOpenFilter = (e: React.MouseEvent<SVGSVGElement, MouseEvent>): void => {
    setIsOpen((state) => !state);
  };

  const handleSelectionChanged = (item: Option): void => {
    onChange(
      selectedItems?.indexOf(item.value) >= 0
        ? selectedItems.filter((el) => el !== item.value)
        : [...selectedItems, item.value]
    );
    setIsOpen(false);
  };

  const handleSelectAll = (): void => {
    selectedItems.length < allItems.length ? onChange(allItems.map(({ value }) => value)) : onChange([]);
    setIsOpen(false);
  };

  return (
    <StyledFilterWrapper>
      <StyledFilterIcon
        data-tooltip-id={elementId}
        data-testid={elementId}
        $active={isOpen || (selectedItems && selectedItems.length > 0)}
        onClick={handleOpenFilter}
      />
      {createPortal(
        <Tooltip
          id={elementId}
          place="bottom"
          style={tooltipStyles}
          isOpen={isOpen}
          setIsOpen={(e): void => {
            if (isOpen) {
              setIsOpen(false);
            }
          }}
          opacity={1}
          globalCloseEvents={globalCloseEvents}
          variant="light"
          className="descriptionTooltip"
          openOnClick
          clickable
          noArrow
        >
          <div data-testid="filterContent">
            {selectAll && (
              <StyledSelectAll data-testid="selectAll" onClick={handleSelectAll}>
                {selectedItems.length < allItems.length ? 'Select All' : 'Unselect All'}
              </StyledSelectAll>
            )}
            {allItems?.map((el) => (
              <StyledCheckboxWrapper key={el.value}>
                <StyledCheckbox
                  data-testid="checkbox"
                  label={el.label}
                  checked={selectedItems && selectedItems?.indexOf(el.value) >= 0}
                  onChange={(): void => handleSelectionChanged(el)}
                />
              </StyledCheckboxWrapper>
            ))}
          </div>
        </Tooltip>,
        document.body
      )}
    </StyledFilterWrapper>
  );
};

export default MultiSelectFilter;
